import './style.scss';

import classNames from 'classnames';
import ButtonC from 'components/ButtonC';
import { sleep } from 'helper/js/helper';
import {
  createRef,
  isValidElement,
  PropsWithChildren,
  PureComponent,
  TransitionEvent,
} from 'react';

type OverlayProps = {
  onClose?: () => void;
  className: string;
};

type OverlayState = {
  show: boolean;
};

const makeCancelable = (promise: any) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject(new Error('cancelled')) : resolve(val)),
      (error) => (hasCanceled_ ? reject(new Error('cancelled')) : reject(error)),
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
};

export default class Overlay extends PureComponent<PropsWithChildren<OverlayProps>, OverlayState> {
  static defaultProps = {
    className: '',
  };

  state = {
    show: false,
  };

  overlayRef = createRef<HTMLDivElement>();

  timer: any = undefined;

  handleKeyPressed = (e: KeyboardEvent) => {
    if (e.keyCode === 27 || (e.type === 'click' && e.target === this.overlayRef.current)) {
      this.handleClose();
    }
    if (e.key === 'Tab' && this.overlayRef.current) {
      // Find all focusable children inside overlay and focus the first one
      const focusables = this.overlayRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusables.length === 0) return;

      const firstElement = focusables[0] as HTMLElement;
      const lastElement = focusables[focusables.length - 1] as HTMLElement;

      if (e.shiftKey) {
        // Shift + Tab, navigate to previous focusable element
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Plain Tab, navigate to next focusable element
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  async componentDidMount() {
    this.timer = makeCancelable(sleep(10));

    this.timer.promise
      .then(() => {
        this.setState({ show: true });
        document.addEventListener('keydown', this.handleKeyPressed, false);
        document.addEventListener('focusin', this.handleFocusIn, false);

        setTimeout(() => {
          this.focusFirstElement();
        }, 150);
      })
      .catch(() => {
        console.log('ERROR');
      });
  }

  handleFocusIn = () => {
    // if the popup is open but not focused yet
    if (
      this.state.show &&
      this.overlayRef.current &&
      !this.overlayRef.current.contains(document.activeElement)
    ) {
      setTimeout(() => {
        this.focusFirstElement();
      }, 50);
    }
  };

  focusFirstElement = () => {
    if (this.overlayRef.current) {
      const first = this.overlayRef.current.querySelector(
        'button, input, [href], [tabindex="0"]',
      ) as HTMLElement;

      if (first) {
        first.focus();
      } else {
        // focus the div
        this.overlayRef.current.tabIndex = -1;
        this.overlayRef.current.focus();
      }
    }
  };

  handleClose = () => {
    this.setState({ show: false });
  };

  handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (
      e.target === this.overlayRef.current &&
      !e.currentTarget.classList.contains('overlay--open')
    ) {
      this.props.onClose?.();
    }
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPressed, false);
  }

  render() {
    const { children, className, onClose } = this.props;
    const overlayClasses = classNames({
      overlay: true,
      [className]: true,
      'overlay--open': isValidElement(children) && this.state.show,
    });
    return (
      <div
        className={overlayClasses}
        ref={this.overlayRef}
        onTransitionEnd={this.handleTransitionEnd}>
        <div className="overlay__button-close">
          {onClose && (
            <ButtonC
              onClick={this.handleClose}
              icon={`cross--${import.meta.env.VITE_THEME}`}
              ariaLabel="close overlay"
            />
          )}
        </div>
        {children}
      </div>
    );
  }
}
